import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { LocationPicker } from '@/components/LocationPicker';

/*
 * AddressDialog — reusable delivery-location editor (same experience as the
 * registration form): label + map picker (search/click/drag) + full address + default.
 * Used by both the customer profile and the admin customer-detail pages.
 */
export const AddressDialog = ({ open, onOpenChange, initial, onSave, title = 'Delivery Location' }) => {
  const [v, setV] = useState({ label: '', address: '', lat: 0, lng: 0, is_default: false });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setV({
        label: initial?.label || '', address: initial?.address || '',
        lat: initial?.lat || 0, lng: initial?.lng || 0, is_default: !!initial?.is_default,
      });
    }
  }, [open, initial]);

  const save = async () => {
    if (!v.address || !v.address.trim()) return;
    setSaving(true);
    try { await onSave(v); onOpenChange(false); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Label</Label><Input value={v.label} onChange={(e) => setV({ ...v, label: e.target.value })} placeholder="e.g. Plant 1, Warehouse" data-testid="addr-label-input" /></div>
          <LocationPicker value={{ address: v.address, lat: v.lat, lng: v.lng }} onChange={(n) => setV({ ...v, ...n })} testId="addr-location-picker" />
          <div><Label>Full Address</Label><Textarea rows={2} value={v.address} onChange={(e) => setV({ ...v, address: e.target.value })} placeholder="House/plot, street, area, city, pincode" data-testid="addr-address-input" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={v.is_default} onCheckedChange={(c) => setV({ ...v, is_default: c })} data-testid="addr-default-switch" />Set as default location</label>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-address-button">{saving ? 'Saving...' : 'Save'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddressDialog;
